import {
  fromLocalToDBStartOfDayTimestamp,
  fromLocalToDBEndOfDayTimestamp,
} from '@pinnacleautomotive/clarity-components';
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import {
  EndPoints,
  ProcessStep,
  StaticBody,
} from '../../../common/enums/enums';
import { errorResponseHelper } from '../../../helpers/apiResponseHelpers';
import { amplifyApi } from '../../../hooks/amplifyApi';
import { workQueueActionTypes } from '../../types';

export const hideCancelConfirmation = createAction(
  workQueueActionTypes.hideCancelConfirmation
);

export const setAllOrdersFiltersByCompany = createAction(
  workQueueActionTypes.setAllOrdersFiltersByCompany
);

export const exportAllOrdersDataPDF = createAsyncThunk(
  workQueueActionTypes.exportAllOrdersDataPDF,
  async ({ companyId, filters, isDoneTab }) => {
    const groupedFilters = getGroupedFilters(companyId, filters, isDoneTab);
    const staticBody = getStaticBody(isDoneTab);

    return await amplifyApi(
      {
        ...staticBody,
        searchQuery: filters.searchQuery,
        groupedFilters,
        filters: [],
      },
      EndPoints.ORDERS.UNAPPROVED.BULK_PDF.ENDPOINT,
      EndPoints.ORDERS.UNAPPROVED.BULK_PDF.METHOD
    );
  }
);

export const exportAllOrdersDataCSV = createAsyncThunk(
  workQueueActionTypes.exportAllOrdersDataCSV,
  async ({ companyId, filters, isDoneTab }) => {
    const groupedFilters = getGroupedFilters(companyId, filters, isDoneTab);
    const staticBody = getStaticBody(isDoneTab);

    return await amplifyApi(
      {
        ...staticBody,
        searchQuery: filters.searchQuery,
        groupedFilters,
        filters: [],
      },
      EndPoints.ORDERS.UNAPPROVED.BULK_CSV.ENDPOINT,
      EndPoints.ORDERS.UNAPPROVED.BULK_CSV.METHOD
    );
  }
);

export const getAllOrdersWithAllFilters = createAsyncThunk(
  workQueueActionTypes.getAllOrdersWithFilters,
  async (
    {
      companyId,
      pageNumber,
      filters,
      isDoneTab,
      isSortEnabled = false,
      sortBy = '',
      sortField = '',
    },
    { rejectWithValue }
  ) => {
    const groupedFilters = getGroupedFilters(companyId, filters, isDoneTab);
    if (isDoneTab)
      groupedFilters.AND[0].AND.push({
        column: 'step',
        value: ProcessStep.DONE,
        operator: 'eq',
      });
    const staticBody = getStaticBody(
      isDoneTab,
      isSortEnabled,
      sortBy,
      sortField
    );

    try {
      const response = await amplifyApi(
        {
          ...staticBody,
          pageNumber,
          searchQuery: filters.searchQuery,
          groupedFilters,
          filters: [],
        },
        EndPoints.ORDERS.UNAPPROVED.GET_ORDERS.ENDPOINT,
        EndPoints.ORDERS.UNAPPROVED.GET_ORDERS.METHOD
      );
      return response;
    } catch (error) {
      return rejectWithValue({
        status: !error.response ? null : error.response?.status,
        token: !error.response ? null : error.response?.data?.token,
      });
    }
  }
);

export const getAllOrders = createAsyncThunk(
  workQueueActionTypes.getAllOrders,
  async ({
    companyId,
    pageNumber,
    searchQuery,
    selectedVendor,
    selectedDepartments,
  }) => {
    const groupedFilters = {
      AND: [
        { AND: [{ column: 'companyId', value: companyId, operator: 'eq' }] },
      ],
    };

    !!selectedVendor &&
      selectedVendor !== 'all' &&
      groupedFilters.AND.push({
        AND: [
          {
            column: 'vendorCompanyId',
            value: selectedVendor,
            operator: 'eq',
          },
        ],
      });

    proccessMultiselectFilter(
      groupedFilters,
      { selectedDepartments },
      'selectedDepartments',
      'departmentId'
    );

    groupedFilters.AND.push({
      AND: [
        {
          column: 'step',
          value: ProcessStep.DECLINED,
          operator: 'ne',
        },
        {
          column: 'step',
          value: ProcessStep.CANCELED,
          operator: 'ne',
        },
        {
          column: 'step',
          value: ProcessStep.DONE,
          operator: 'lt',
        },
      ],
    });

    return await amplifyApi(
      {
        ...StaticBody.workQueue.allOrders(),
        pageNumber,
        searchQuery,
        groupedFilters,
      },
      EndPoints.ORDERS.UNAPPROVED.GET_ORDERS.ENDPOINT,
      EndPoints.ORDERS.UNAPPROVED.GET_ORDERS.METHOD
    );
  }
);

export const cancelStepChangeStatusAllOrders = createAsyncThunk(
  workQueueActionTypes.cancelStepStatusChangeAllOrders,
  async ({ vendorOrderId, cancellingNote: notes }, { rejectWithValue }) => {
    try {
      const response = await amplifyApi(
        { notes },
        EndPoints.ORDERS.CANCEL_ORDER.ENDPOINT.replace(
          '{vendor_order_id}',
          vendorOrderId
        ),
        EndPoints.ORDERS.CANCEL_ORDER.TYPE,
        true
      );
      return { status: response.status, data: response.data };
    } catch (error) {
      return rejectWithValue(errorResponseHelper(error));
    }
  }
);

export const getGroupedFiltersBase = (
  groupedFilters,
  filters,
  isDoneTab,
  isVehicle
) => {
  proccessMultiselectFilter(
    groupedFilters,
    filters,
    'selectedDepartments',
    'departmentId'
  );

  if (!isVehicle)
    proccessMultiselectFilter(
      groupedFilters,
      filters,
      'selectedVendors',
      'vendorCompanyId'
    );

  if (isVehicle) proccessVendorCompaniesVehicles(groupedFilters, filters);

  proccessMultiselectFilter(groupedFilters, filters, 'selectedSteps', 'step');

  const columnDate = (isDoneTab && 'completedOn') || 'date';

  if (!!filters.dateRange && filters.dateRange?.startingDate) {
    const formattedStartDate = fromLocalToDBStartOfDayTimestamp(
      filters.dateRange.startingDate
    );
    groupedFilters.AND[0].AND.push({
      column: columnDate,
      value: formattedStartDate,
      operator: 'gte',
    });
  }

  if (!!filters.dateRange && filters.dateRange?.endDate) {
    const formattedEndDate = fromLocalToDBEndOfDayTimestamp(
      filters.dateRange.endDate
    );

    groupedFilters.AND[0].AND.push({
      column: columnDate,
      value: formattedEndDate,
      operator: 'lte',
    });
  }

  return groupedFilters;
};

export const getGroupedFiltersForVehicle = (companyId, filters, isDoneTab) => {
  const groupedFilters = {
    AND: [
      {
        AND: [{ column: 'dealershipId', value: companyId, operator: 'eq' }],
      },
    ],
  };

  if (isDoneTab)
    groupedFilters.AND[0].AND.push({
      column: 'step',
      value: ProcessStep.DONE,
      operator: 'eq',
    });

  return getGroupedFiltersBase(groupedFilters, filters, isDoneTab, true);
};

export const getGroupedFilters = (companyId, filters, isDoneTab) => {
  const groupedFilters = {
    AND: [{ AND: [{ column: 'companyId', value: companyId, operator: 'eq' }] }],
  };

  return getGroupedFiltersBase(groupedFilters, filters, isDoneTab);
};

function getStaticBody(
  isDoneTab,
  isSortEnabled = false,
  sortBy = '',
  sortField = ''
) {
  return isDoneTab
    ? StaticBody.workQueue.done
    : StaticBody.workQueue.allOrders(isSortEnabled, sortBy, sortField);
}

function proccessMultiselectFilter(groupedFilters, filters, filterBy, column) {
  !!filters[filterBy]?.length &&
    filters[filterBy][0] !== 'all' &&
    groupedFilters.AND.push({
      OR: [
        ...filters[filterBy].map(step => ({
          column: column,
          value: step,
          operator: 'eq',
        })),
      ],
    });
}

function proccessVendorCompaniesVehicles(groupedFilters, filters) {
  !!filters.selectedVendors?.length &&
    filters.selectedVendors[0] !== 'all' &&
    groupedFilters.AND.push({
      AND: [
        {
          column: 'vendorIds',
          value: [...filters.selectedVendors],
          operator: 'in',
        },
      ],
    });
}

export const setAllOrdersSortDetails = createAction(
  workQueueActionTypes.setAllOrdersSortDetails
);

export const setAllOrdersHasMore = createAction(
  workQueueActionTypes.setAllOrdersHasMore
);

export const setAllOrdersStatus = createAction(
  workQueueActionTypes.setAllOrdersStatus
);
